/**
 * Course for several exams, method module and mother tongue (D-107): pure logic shared by the web
 * and the phone. The TCF and the IELTS use the same course screens; only the texts, the language
 * of the content and the voice change. No React and no platform API here.
 */

import { type LocalizedText, localizedText } from "../ielts/course";
import { isObject, num, objects, str } from "../ielts/read";

export type CourseExam = "tcf_canada" | "ielts_general" | "tef_canada" | "goethe";
/** The language of the exam content (D-118: German for the Goethe-Zertifikat). */
export type ContentLanguage = "fr" | "en" | "de";

/** The exam content keeps its language: French for the TCF and the TEF, English for the IELTS,
 * German for the Goethe-Zertifikat. */
export function contentLanguage(exam: string): ContentLanguage {
  return exam === "ielts_general" ? "en" : exam === "goethe" ? "de" : "fr";
}

/** The voice of the documents, scripts and vocabulary: fr-FR for the TCF, en-GB for the IELTS,
 * de-DE for German. */
export function contentVoice(exam: string): string {
  return exam === "ielts_general" ? "en-GB" : exam === "goethe" ? "de-DE" : "fr-FR";
}

/** The language of a lesson's explanation: the content's for the TCF, TEF and IELTS; for German
 * (whose explanations are written in French and English) the learner's interface language. */
export function explanationLanguage(exam: string, interfaceLanguage = "fr"): "fr" | "en" {
  if (exam === "goethe") return interfaceLanguage.startsWith("en") ? "en" : "fr";
  return exam === "ielts_general" ? "en" : "fr";
}

/** The help shown beside the content: French for the IELTS, English for the TCF; for German, the
 * other of French and English. */
export function helpLanguage(exam: string, interfaceLanguage = "fr"): "fr" | "en" {
  if (exam === "goethe") return explanationLanguage(exam, interfaceLanguage) === "fr" ? "en" : "fr";
  return exam === "ielts_general" ? "fr" : "en";
}

// --- Mother tongues ------------------------------------------------------------------------

export interface SpokenLanguage {
  code: string;
  name: LocalizedText;
  nativeName: string;
  beta: boolean;
  serverVoice: boolean;
  deviceLocale: string;
  rtl: boolean;
  fallback: string;
}

export function parseLanguages(value: unknown): SpokenLanguage[] {
  return objects(value)
    .map((row) => ({
      code: str(row.code),
      name: localizedText(row.name),
      nativeName: str(row.native_name),
      beta: row.beta === true,
      serverVoice: row.server_voice === true,
      deviceLocale: str(row.device_locale),
      rtl: row.rtl === true,
      fallback: str(row.fallback),
    }))
    .filter((row) => row.code);
}

/** « Ewondo (Ewondo) », « Arabic (العربية) »: the name in the interface, then in the language. */
export function languageLabel(language: SpokenLanguage, interfaceLanguage: string): string {
  const lang = interfaceLanguage.split("-")[0] ?? "fr";
  const name = language.name[lang] || language.name.en || language.code;
  return language.nativeName && language.nativeName !== name ? `${name} (${language.nativeName})` : name;
}

// --- Translation of a lesson ---------------------------------------------------------------

export type TranslationStatus = "none" | "pending" | "ready" | "failed" | "unavailable";

export interface LessonTranslation {
  language: string;
  status: TranslationStatus;
  /** ai (automatic translation), original (the lesson's own language) or fallback. */
  source: string;
  beta: boolean;
  fallbackLanguage: string;
  message: string;
  title: string;
  explanation: string;
  objectives: string[];
  vocabulary: Record<string, string>;
  doList: string[];
  avoidList: string[];
  exercises: Record<string, string>;
}

const STATUSES: readonly TranslationStatus[] = ["none", "pending", "ready", "failed", "unavailable"];

export function parseTranslation(value: unknown): LessonTranslation {
  const data = isObject(value) ? value : {};
  const content = isObject(data.content) ? data.content : {};
  const status = STATUSES.find((s) => s === data.status) ?? "none";
  return {
    language: str(data.language),
    status,
    source: str(data.source),
    beta: data.beta === true,
    fallbackLanguage: str(data.fallback_language),
    message: str(data.message),
    title: str(content.title),
    explanation: str(content.explanation),
    objectives: Array.isArray(content.objectives) ? content.objectives.map((o) => str(o)).filter(Boolean) : [],
    vocabulary: Object.fromEntries(objects(content.vocabulary).map((v) => [str(v.word), str(v.meaning)])),
    doList: Array.isArray(content.do) ? content.do.map((o) => str(o)).filter(Boolean) : [],
    avoidList: Array.isArray(content.avoid) ? content.avoid.map((o) => str(o)).filter(Boolean) : [],
    exercises: Object.fromEntries(objects(content.exercises).map((e) => [str(e.id), str(e.explanation)])),
  };
}

/** The translation is being made: ask again in a moment. */
export function translationPending(t: LessonTranslation): boolean {
  return t.status === "pending";
}

// --- Listen to the explanation -------------------------------------------------------------

export interface ListenPlan {
  language: string;
  /** server (Piper audio), device (the device's voice of `locale`) or fallback (fr / en). */
  mode: "server" | "device" | "fallback";
  locale: string;
  text: string;
  status: "ready" | "pending";
  audioUrl: string | null;
  fallbackLocale: string;
  fallbackText: string;
  message: string;
}

export function parseListen(value: unknown): ListenPlan {
  const data = isObject(value) ? value : {};
  const mode = data.mode === "server" || data.mode === "device" ? data.mode : "fallback";
  return {
    language: str(data.language),
    mode,
    locale: str(data.locale),
    text: str(data.text),
    status: data.status === "pending" ? "pending" : "ready",
    audioUrl: str(data.audio_url) || null,
    fallbackLocale: str(data.fallback_locale) || "fr-FR",
    fallbackText: str(data.fallback_text),
    message: str(data.message),
  };
}

interface VoiceLike {
  lang?: string;
  language?: string;
}

const tag = (v: VoiceLike) => (v.lang ?? v.language ?? "").replace("_", "-").toLowerCase();

/** The device's voice of a locale: the exact one first, then any voice of the same language. */
export function pickVoice<V extends VoiceLike>(list: readonly V[], locale: string): V | undefined {
  const wanted = locale.replace("_", "-").toLowerCase();
  const language = wanted.split("-")[0] ?? "";
  if (!language) return undefined;
  return list.find((v) => tag(v) === wanted) ?? list.find((v) => tag(v).split("-")[0] === language);
}

/** What the device reads: the plan's language when it has that voice, else French/English. */
export function deviceSpeech<V extends VoiceLike>(
  plan: ListenPlan,
  voices: readonly V[],
): { text: string; locale: string; voice: V | undefined; fallback: boolean } {
  if (plan.mode !== "fallback") {
    const voice = pickVoice(voices, plan.locale);
    if (voice) return { text: plan.text, locale: plan.locale, voice, fallback: false };
  }
  const locale = plan.mode === "fallback" ? plan.locale || plan.fallbackLocale : plan.fallbackLocale;
  const text = plan.mode === "fallback" ? plan.text || plan.fallbackText : plan.fallbackText;
  return { text, locale, voice: pickVoice(voices, locale), fallback: true };
}

// --- Dialogue scripts ----------------------------------------------------------------------
// The lines of a listening script and one voice per character come from `../listening`
// (`splitScript`, `planVoices`, the CO module, D-106): the course reads its scripts with them.

// --- Method module -------------------------------------------------------------------------

export interface AppliesTo {
  skill: string;
  task: string;
  type: string;
}

export interface DoAvoid {
  doList: string[];
  avoidList: string[];
}

function point(value: unknown, language: string): string {
  if (typeof value === "string") return value;
  if (!isObject(value)) return "";
  const lang = language.split("-")[0] ?? "fr";
  return str(value[lang]) || str(value.fr) || str(value.en);
}

/** « À faire / À éviter » of a method lesson ({do, avoid}, each point a text or {fr, en}). */
export function parseDoAvoid(value: unknown, language: string): DoAvoid {
  const data = isObject(value) ? value : {};
  const list = (v: unknown) => (Array.isArray(v) ? v.map((p) => point(p, language)).filter(Boolean) : []);
  return { doList: list(data.do), avoidList: list(data.avoid) };
}

export function parseAppliesTo(value: unknown): AppliesTo[] {
  return objects(value).map((a) => ({ skill: str(a.skill), task: str(a.task), type: str(a.type) }));
}

export interface MethodBlock {
  open: boolean;
  free: boolean;
  lessons: number;
  title: LocalizedText;
  units: { id: string; code: string; title: LocalizedText; lessons: { id: string; code: string; title: LocalizedText; state: string }[] }[];
}

export function parseMethod(value: unknown): MethodBlock {
  const data = isObject(value) ? value : {};
  return {
    open: data.open !== false,
    free: data.free === true,
    lessons: num(data.lessons),
    title: localizedText(data.title),
    units: objects(data.units).map((u) => ({
      id: str(u.id),
      code: str(u.code),
      title: localizedText(u.title),
      lessons: objects(u.lessons).map((l) => ({ id: str(l.id), code: str(l.code), title: localizedText(l.title), state: str(l.state) })),
    })),
  };
}

/** The query of « How to succeed in this type of question? » for the current question. */
export function methodQuery(exam: string, skill: string, detail: { task?: number | string | null; type?: string | null } = {}): { exam?: CourseExam; skill: string; task?: string; type?: string } {
  const out: { exam?: CourseExam; skill: string; task?: string; type?: string } = { skill };
  // every exam but the TCF (the API's default) says its code, German included (D-118)
  if (exam === "ielts_general" || exam === "tef_canada" || exam === "goethe") out.exam = exam;
  if (detail.task !== undefined && detail.task !== null && String(detail.task)) out.task = String(detail.task);
  if (detail.type) out.type = detail.type;
  return out;
}

// --- Model answers -------------------------------------------------------------------------

/** A TCF model answer ends with « \n\n---\nAnnotation : … »: the answer, then the annotation. */
export function splitModelAnswer(value: string): { answer: string; annotation: string } {
  const match = /\n\s*-{3,}\s*\n\s*(?:Annotation|Note)\s*:\s*/iu.exec(value);
  if (!match) return { answer: value.trim(), annotation: "" };
  return { answer: value.slice(0, match.index).trim(), annotation: value.slice(match.index + match[0].length).trim() };
}

/** The level aimed at, as the exam says it: « NCLC 7-8 » for the TCF, « Band 6.0-6.5 » for the IELTS. */
export function levelTarget(exam: string, target: string): { kind: "nclc" | "band"; value: string } | null {
  if (!target || target === "all levels" || exam === "goethe") return null; // German: the CEFR badge says it
  return { kind: exam === "ielts_general" ? "band" : "nclc", value: target }; // TEF: NCLC too
}

// --- True / false / not given in the exam language -----------------------------------------

const TFNG_FRENCH: Record<string, string> = { TRUE: "VRAI", FALSE: "FAUX", "NOT GIVEN": "NON MENTIONNÉ" };
const TFNG_GERMAN: Record<string, string> = { TRUE: "RICHTIG", FALSE: "FALSCH", "NOT GIVEN": "NICHT IM TEXT" };
const JA_NEIN: Record<string, string> = { TRUE: "JA", FALSE: "NEIN" };

/** « TRUE » is shown « VRAI » in a TCF exercise, « RICHTIG » (or « JA ») in a German one; the
 * answer sent stays TRUE / FALSE / NOT GIVEN. */
export function tfngLabel(option: string, language: string, style = ""): string {
  if (style === "ja_nein") return JA_NEIN[option] ?? option;
  if (language === "de") return TFNG_GERMAN[option] ?? option;
  return language === "fr" ? (TFNG_FRENCH[option] ?? option) : option;
}

/** The buttons of a true / false item: three, or two for a German « Ja / Nein » (D-118). */
export function tfngOptions(style = ""): readonly string[] {
  return style === "ja_nein" ? ["TRUE", "FALSE"] : ["TRUE", "FALSE", "NOT GIVEN"];
}
